import {
  createFileRoute,
  FileRouteTypes,
  Link,
  Outlet,
} from "@tanstack/react-router";
import {
  CommandLineIcon,
  ArrowRightStartOnRectangleIcon,
  AcademicCapIcon,
  BanknotesIcon,
  BookOpenIcon,
  ChartBarIcon,
  FolderIcon,
  ArrowUpRightIcon,
  MapIcon,
  EnvelopeIcon,
  KeyIcon,
  Cog8ToothIcon,
  ClipboardDocumentListIcon,
  InformationCircleIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowLeftIcon,
  SparklesIcon,
  PlusIcon,
  LanguageIcon,
  CameraIcon,
  MicrophoneIcon,
  PencilSquareIcon,
  QuestionMarkCircleIcon,
} from "@heroicons/react/24/outline";

import BrandLogo from "../../assets/brand-logo.svg?react";

import { UserIcon } from "@heroicons/react/24/solid";

import { useLoginStatus } from "../../hooks/useLoginStatus";
import { LoginComponent } from "../../components/LoginComponent";
import {
  CSSProperties,
  JSX,
  type PointerEvent as ReactPointerEvent,
  useEffect,
  useRef,
  useState,
} from "react";
import { useUserSettings } from "../../hooks/useUserSettings";
import { useLogin } from "../../hooks/useLogin";
import useTranslation from "../../hooks/useTranslation";
import { SnackBar } from "../../components/SnackBar";
import ProgressBar from "../../components/ProgressBar";
import { useUserProfileSendHeartbeat } from "../../hooks/useUserProfileSendHeartbeat";
import { useFrontendRefreshOnNewVersion } from "../../hooks/useFrontendRefreshOnNewVersion";
import {
  useLanguageSpace,
  type LanguageSpace,
} from "../../hooks/useLanguageSpace";
import { ENABLED_LANGUAGE_SPACE_IDS, TEXT_LANGUAGES } from "../../constants";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import {
  deriveEffectiveLanguageSpaceScope,
  getAddedLanguageSpacesStorageKey,
} from "../../hooks/useAddedLanguageSpaces";
import {
  maybeConsumeSpeakingTriggerCue,
  setActiveSpeakingTrigger,
  useSpeakingTriggerCue,
} from "../../hooks/useSpeakingTriggers";
import { useNavigate, useRouterState } from "@tanstack/react-router";

export const Route = createFileRoute("/app")({
  component: AppLayout,
});

function AppLayout() {
  const loginStatus = useLoginStatus();

  if (!loginStatus.isSuccess) {
    return <></>;
  }

  if (loginStatus.data.state === "logged out") {
    return <LoginComponent />;
  }
  return <AppLayoutImpl />;
}

type LinkTarget = FileRouteTypes["to"];

interface NavItem {
  link: LinkTarget;
  text: string;
  pageTitle?: string;
  icon: JSX.Element;
}

type MobileLanguageCarouselTheme = {
  surface: string;
  border: string;
  ink: string;
  wash: string;
  pageWash: string;
  contentWash: string;
};

const MOBILE_LANGUAGE_CAROUSEL_STEP_PX = 122;
const MOBILE_LANGUAGE_CAROUSEL_CARD_WIDTH_PX = 132;
const MOBILE_LANGUAGE_CAROUSEL_DRAG_THRESHOLD_PX = 54;

const MOBILE_LANGUAGE_CAROUSEL_NAME_KEYS: Partial<
  Record<LanguageSpace, string>
> = {
  ENGLISH: "mobile_language_space_endonym_english",
  GERMAN: "mobile_language_space_endonym_german",
  FRENCH: "mobile_language_space_endonym_french",
  ITALIAN: "mobile_language_space_endonym_italian",
};

const MOBILE_LANGUAGE_CAROUSEL_THEME_BY_SPACE: Partial<
  Record<LanguageSpace, MobileLanguageCarouselTheme>
> = {
  ENGLISH: {
    surface: "rgba(231, 236, 225, 0.82)",
    border: "rgba(190, 199, 185, 0.72)",
    ink: "#2f3b35",
    wash: "linear-gradient(180deg, rgba(245,241,228,0.96) 0%, rgba(213,220,206,0.8) 40%, rgba(181,192,183,0.46) 72%, rgba(248,250,252,0.14) 100%), radial-gradient(circle at 78% 10%, rgba(255,250,234,0.34) 0%, rgba(255,250,234,0) 28%)",
    pageWash:
      "linear-gradient(180deg, rgba(244,240,228,0.8) 0px, rgba(210,219,206,0.34) 182px, rgba(248,250,252,0.96) 430px), radial-gradient(circle at 78% 0%, rgba(255,250,234,0.24) 0%, rgba(255,250,234,0) 28%)",
    contentWash:
      "linear-gradient(180deg, rgba(237,232,226,0.34) 0px, rgba(192,203,193,0.18) 130px, rgba(248,250,252,0) 300px)",
  },
  FRENCH: {
    surface: "rgba(248, 234, 208, 0.84)",
    border: "rgba(226, 190, 152, 0.82)",
    ink: "#7f4328",
    wash: "linear-gradient(180deg, rgba(245,241,228,0.96) 0%, rgba(239,219,189,0.8) 40%, rgba(228,190,157,0.46) 72%, rgba(248,250,252,0.14) 100%), radial-gradient(circle at 78% 10%, rgba(255,250,234,0.34) 0%, rgba(255,250,234,0) 28%)",
    pageWash:
      "linear-gradient(180deg, rgba(244,240,228,0.8) 0px, rgba(236,211,178,0.34) 182px, rgba(248,250,252,0.96) 430px), radial-gradient(circle at 78% 0%, rgba(255,250,234,0.24) 0%, rgba(255,250,234,0) 28%)",
    contentWash:
      "linear-gradient(180deg, rgba(237,232,226,0.34) 0px, rgba(228,195,154,0.18) 130px, rgba(248,250,252,0) 300px)",
  },
  GERMAN: {
    surface: "rgba(252, 242, 220, 0.84)",
    border: "rgba(222, 200, 160, 0.82)",
    ink: "#7b4a1e",
    wash: "linear-gradient(180deg, rgba(245,241,228,0.96) 0%, rgba(238,215,177,0.8) 40%, rgba(218,186,143,0.46) 72%, rgba(248,250,252,0.14) 100%), radial-gradient(circle at 78% 10%, rgba(255,250,234,0.34) 0%, rgba(255,250,234,0) 28%)",
    pageWash:
      "linear-gradient(180deg, rgba(244,240,228,0.8) 0px, rgba(238,215,177,0.34) 182px, rgba(248,250,252,0.96) 430px), radial-gradient(circle at 78% 0%, rgba(255,250,234,0.24) 0%, rgba(255,250,234,0) 28%)",
    contentWash:
      "linear-gradient(180deg, rgba(237,232,226,0.34) 0px, rgba(218,186,143,0.18) 130px, rgba(248,250,252,0) 300px)",
  },
  ITALIAN: {
    surface: "rgba(233, 226, 242, 0.84)",
    border: "rgba(192, 176, 220, 0.82)",
    ink: "#5a4b83",
    wash: "linear-gradient(180deg, rgba(245,241,228,0.96) 0%, rgba(218,208,234,0.8) 40%, rgba(189,177,219,0.46) 72%, rgba(248,250,252,0.14) 100%), radial-gradient(circle at 78% 10%, rgba(255,250,234,0.34) 0%, rgba(255,250,234,0) 28%)",
    pageWash:
      "linear-gradient(180deg, rgba(244,240,228,0.8) 0px, rgba(208,196,228,0.34) 182px, rgba(248,250,252,0.96) 430px), radial-gradient(circle at 78% 0%, rgba(255,250,234,0.24) 0%, rgba(255,250,234,0) 28%)",
    contentWash:
      "linear-gradient(180deg, rgba(237,232,226,0.34) 0px, rgba(197,183,223,0.18) 130px, rgba(248,250,252,0) 300px)",
  },
};

function clampNumber(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function getCircularStepDelta(
  index: number,
  activeIndex: number,
  total: number,
) {
  if (total <= 1) {
    return 0;
  }
  const forward = (index - activeIndex + total) % total;
  const backward = forward - total;
  return Math.abs(forward) <= Math.abs(backward) ? forward : backward;
}

function getMobileLanguageCarouselLabel(
  id: LanguageSpace,
  fallbackLabel: string,
  translate: (key: string) => string,
) {
  const key = MOBILE_LANGUAGE_CAROUSEL_NAME_KEYS[id];
  return key ? translate(key) : fallbackLabel;
}

function getMobileLanguageCarouselTheme(
  id: LanguageSpace | undefined,
  fallbackAccentBg: string | undefined,
  fallbackAccentText: string | undefined,
): MobileLanguageCarouselTheme {
  if (id && MOBILE_LANGUAGE_CAROUSEL_THEME_BY_SPACE[id]) {
    return MOBILE_LANGUAGE_CAROUSEL_THEME_BY_SPACE[id];
  }

  return {
    surface: fallbackAccentBg ?? "#F8FAFC",
    border: "#E2E8F0",
    ink: fallbackAccentText ?? "#0F172A",
    wash: "linear-gradient(180deg, rgba(241,245,249,0.94) 0%, rgba(229,235,241,0.74) 34%, rgba(214,223,233,0.32) 76%, rgba(248,250,252,0.12) 100%)",
    pageWash:
      "linear-gradient(180deg, rgba(241,245,249,0.78) 0px, rgba(226,232,240,0.3) 180px, rgba(248,250,252,0.96) 430px)",
    contentWash:
      "linear-gradient(180deg, rgba(241,245,249,0.28) 0px, rgba(226,232,240,0.14) 128px, rgba(248,250,252,0) 300px)",
  };
}

function AppLayoutImpl() {
  const [navbarOpen, setNavbarOpen] = useState(false);

  const loginStatus = useLoginStatus();
  const userSettings = useUserSettings();
  const navigate = useNavigate();
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const search = useRouterState({
    select: (state) => state.location.searchStr,
  });
  const isSettingsPage =
    pathname === "/app/space_settings" || pathname === "/app/settings";
  const cameFromSpaceSettings = search.includes("from=space_settings");
  const isInSettingsFlow = isSettingsPage || cameFromSpaceSettings;
  const isOnTextDetailsPage =
    pathname.startsWith("/app/reeprise/") && pathname !== "/app/reeprise";
  const isOnStartLearningPage = pathname === "/app/start-learning";
  const isOnMainAppPage = pathname === "/app/reeprise";
  const showMobileLanguageCarousel = pathname === "/app/reeprise";
  const isOnMobileCreateView =
    pathname === "/app/reeprise" && search.includes("mobileView=create");
  const showMobileBottomNav =
    pathname === "/app/reeprise" ||
    pathname === "/app/quick_translate" ||
    pathname === "/app/vocabulary_progress" ||
    pathname === "/app/profile";
  const hideTopActionControl = navbarOpen;
  const hideNavbarAuxControls = navbarOpen || isOnTextDetailsPage;
  const showLanguageSpaceSwitch =
    !isInSettingsFlow && !isOnTextDetailsPage && !isOnStartLearningPage;
  const topActionVisibilityClass = hideTopActionControl
    ? "opacity-0 pointer-events-none"
    : "opacity-100";
  const mobileBackTarget = cameFromSpaceSettings
    ? "/app/space_settings"
    : isSettingsPage
      ? "/app/profile"
      : "/app/reeprise";
  useFrontendRefreshOnNewVersion();
  const { mutateAsync: sendHeartbeat } = useUserProfileSendHeartbeat();
  const login = useLogin();

  const translation = useTranslation();
  const { languageSpace, setLanguageSpace } = useLanguageSpace("ENGLISH");
  const userAddedLanguageSpacesStorageKey = getAddedLanguageSpacesStorageKey(
    loginStatus.data?.state === "logged in" ? loginStatus.data.userId : null,
  );
  const [addedLanguageSpaces] = useLocalStorage<string[]>(
    userAddedLanguageSpacesStorageKey,
    [],
  );
  const speakingTriggerCue = useSpeakingTriggerCue();
  const [speakingCueMode] = useLocalStorage<"off" | "subtle" | "prominent">(
    "speaking-cue-mode-v1",
    "subtle",
  );
  const [lastInteractionAt, setLastInteractionAt] = useState<number>(Date.now());
  const [nowTs, setNowTs] = useState<number>(Date.now());
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
  const languageMenuRef = useRef<HTMLDivElement | null>(null);
  const serverAddedLanguageSpaces = Array.isArray(
    userSettings.data?.added_language_spaces,
  )
    ? userSettings.data.added_language_spaces
    : [];
  const hasPersistedServerAddedLanguageSpaces =
    serverAddedLanguageSpaces.length > 0;
  const onboardingCompleted = userSettings.data?.onboarding_completed === true;
  const hasPersistedLocalAddedLanguageSpaces =
    typeof window !== "undefined" &&
    window.localStorage.getItem(userAddedLanguageSpacesStorageKey) !== null;
  const hasPersistedAddedLanguageSpaces =
    hasPersistedServerAddedLanguageSpaces || hasPersistedLocalAddedLanguageSpaces;
  const effectiveAddedLanguageSpaces =
    hasPersistedServerAddedLanguageSpaces
      ? serverAddedLanguageSpaces
      : addedLanguageSpaces;
  const isForcedStartLearningOnboarding =
    isOnStartLearningPage &&
    (userSettings.isSuccess ? !onboardingCompleted : true);
  const shouldShowTopbarBackButton =
    !isOnMainAppPage && !isForcedStartLearningOnboarding;
  const {
    effectiveLanguageSpaceIds,
    effectiveLanguageSpaceSet,
    hasAddedAllEnabledSpaces,
  } = deriveEffectiveLanguageSpaceScope({
    addedLanguageSpaces: effectiveAddedLanguageSpaces,
    enabledLanguageSpaceIds: ENABLED_LANGUAGE_SPACE_IDS,
    hasPersistedAddedLanguageSpaces,
    fallbackLanguageSpaceId: languageSpace === "ALL" ? "ENGLISH" : languageSpace,
  });
  const languageOptions = TEXT_LANGUAGES
    .filter((lang) => effectiveLanguageSpaceSet.has(lang.id))
    .map((lang) => ({
      id: lang.id as LanguageSpace,
      label: translation.translate(lang.prettyTranslationKey),
      accentBg: lang.accentBg,
      accentText: lang.accentText,
    }));
  const currentLanguageIndex = languageOptions.findIndex(
    (opt) => opt.id === languageSpace,
  );
  const currentLanguage = languageOptions[currentLanguageIndex];
  const currentLanguageLabel =
    currentLanguage?.id === "GREEK_LATIN_TRANSCRIPTION"
      ? translation.translate("language_greek")
      : currentLanguage?.id === "RUSSIAN_LATIN_TRANSCRIPTION"
        ? translation.translate("language_russian")
      : currentLanguage?.id === "PORTUGUESE_FROM_BRAZIL" ||
          currentLanguage?.id === "PORTUGUESE_FROM_PORTUGAL"
        ? translation.translate("language_portuguese")
      : currentLanguage?.label ?? translation.translate("language_english");
  const [mobileLanguageCarouselOffset, setMobileLanguageCarouselOffset] =
    useState(0);
  const [isMobileLanguageCarouselDragging, setIsMobileLanguageCarouselDragging] =
    useState(false);
  const mobileLanguageCarouselPointerIdRef = useRef<number | null>(null);
  const mobileLanguageCarouselStartXRef = useRef(0);
  const mobileLanguageCarouselMovedRef = useRef(false);
  useEffect(() => {
    let stopped = false;
    (async () => {
      while (!stopped) {
        try {
          // only send the heartbeat if the document is visible
          if (document.visibilityState !== "visible") {
            await new Promise((resolve) => setTimeout(resolve, 5_000));
            continue;
          }
          await sendHeartbeat();
        } catch (error) {
          console.error("Failed to send heartbeat:", error);
        }
        await new Promise((resolve) => setTimeout(resolve, 30_000));
      }
    })();
    return () => {
      stopped = true;
    };
  }, [sendHeartbeat]);

  useEffect(() => {
    if (!languageMenuOpen) return;
    const handleOutsideClick = (event: MouseEvent) => {
      if (!languageMenuRef.current) return;
      if (!languageMenuRef.current.contains(event.target as Node)) {
        setLanguageMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [languageMenuOpen]);

  useEffect(() => {
    if (hideNavbarAuxControls) {
      setLanguageMenuOpen(false);
    }
  }, [hideNavbarAuxControls]);

  useEffect(() => {
    const ids = languageOptions.map((opt) => opt.id);
    if (ids.length === 0) {
      return;
    }
    if (!ids.includes(languageSpace)) {
      const fallbackSpace = ids[0];
      if (fallbackSpace) {
        setLanguageSpace(fallbackSpace);
      }
    }
  }, [languageOptions, languageSpace, setLanguageSpace]);

  useEffect(() => {
    if (pathname === "/app/start-learning") {
      return;
    }
    if (!userSettings.isSuccess) {
      return;
    }
    if (userSettings.data.onboarding_completed !== false) {
      return;
    }
    void navigate({ to: "/app/start-learning" });
  }, [navigate, pathname, userSettings.data, userSettings.isSuccess]);

  useEffect(() => {
    if (
      !hasPersistedAddedLanguageSpaces ||
      effectiveLanguageSpaceIds.length === 0 ||
      effectiveLanguageSpaceSet.has(languageSpace)
    ) {
      return;
    }
    const fallbackSpace = effectiveLanguageSpaceIds[0];
    if (!fallbackSpace) {
      return;
    }
    setLanguageSpace(fallbackSpace as LanguageSpace);
  }, [
    effectiveLanguageSpaceIds,
    effectiveLanguageSpaceSet,
    hasPersistedAddedLanguageSpaces,
    languageSpace,
    setLanguageSpace,
  ]);

  useEffect(() => {
    const markInteraction = () => setLastInteractionAt(Date.now());
    window.addEventListener("pointerdown", markInteraction);
    window.addEventListener("keydown", markInteraction);
    window.addEventListener("touchstart", markInteraction);
    window.addEventListener("scroll", markInteraction, { passive: true });
    const timer = window.setInterval(() => setNowTs(Date.now()), 5000);
    return () => {
      window.removeEventListener("pointerdown", markInteraction);
      window.removeEventListener("keydown", markInteraction);
      window.removeEventListener("touchstart", markInteraction);
      window.removeEventListener("scroll", markInteraction);
      window.clearInterval(timer);
    };
  }, []);

  if (loginStatus.data?.state !== "logged in") {
    return <div />;
  }

  const cueSnoozedForSession =
    typeof window !== "undefined" &&
    window.sessionStorage.getItem("speaking-cue-snooze-session-v1") === "1";
  const isFocusFlowRoute =
    pathname.startsWith("/app/reeprise/") ||
    pathname.startsWith("/app/vocabulary_trainer") ||
    pathname.startsWith("/app/vocabulary_training_mode") ||
    pathname.startsWith("/app/dialogue");
  const isIdleForCue = nowTs - lastInteractionAt > 25_000;
  const showSpeakingCuePulse =
    Boolean(speakingTriggerCue.top) &&
    speakingCueMode !== "off" &&
    !cueSnoozedForSession &&
    !isFocusFlowRoute &&
    isIdleForCue;

  const show_experimental =
    loginStatus.data.is_staff &&
    userSettings.data?.show_experimental_features === true;
  const activeMockMobileLanguage =
    languageOptions.find((opt) => opt.id === languageSpace) ??
    currentLanguage;
  const activeMobileLanguageTheme = getMobileLanguageCarouselTheme(
    activeMockMobileLanguage?.id,
    activeMockMobileLanguage?.accentBg,
    activeMockMobileLanguage?.accentText,
  );
  const activeMobileLanguageIndex = languageOptions.findIndex(
    (opt) => opt.id === languageSpace,
  );

  const handleNavbarToggle = () => {
    setNavbarOpen((prev) => {
      const next = !prev;
      if (next) {
        setLanguageMenuOpen(false);
      }
      return next;
    });
  };
  const closeNavbarOnMobile = () => {
    if (typeof window === "undefined") {
      return;
    }
    if (!window.matchMedia("(max-width: 767px)").matches) {
      return;
    }
    setNavbarOpen(false);
  };
  const navigateBackFromTopbar = () => {
    if (window.history.length > 1) {
      window.history.back();
      return;
    }
    navigate({ to: mobileBackTarget });
  };
  const openAddLanguageSpaceFlow = () => {
    setLanguageMenuOpen(false);
    void navigate({
      to: "/app/start-learning",
    });
  };
  const handleMobileLanguageCarouselPointerDown = (
    event: ReactPointerEvent<HTMLDivElement>,
  ) => {
    if (languageOptions.length <= 1) {
      return;
    }
    mobileLanguageCarouselPointerIdRef.current = event.pointerId;
    mobileLanguageCarouselStartXRef.current = event.clientX;
    mobileLanguageCarouselMovedRef.current = false;
    setIsMobileLanguageCarouselDragging(true);
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const handleMobileLanguageCarouselPointerMove = (
    event: ReactPointerEvent<HTMLDivElement>,
  ) => {
    if (mobileLanguageCarouselPointerIdRef.current !== event.pointerId) {
      return;
    }
    const delta = clampNumber(
      event.clientX - mobileLanguageCarouselStartXRef.current,
      -MOBILE_LANGUAGE_CAROUSEL_STEP_PX,
      MOBILE_LANGUAGE_CAROUSEL_STEP_PX,
    );
    if (Math.abs(delta) > 6) {
      mobileLanguageCarouselMovedRef.current = true;
    }
    setMobileLanguageCarouselOffset(delta);
  };
  const finishMobileLanguageCarouselGesture = (
    event: ReactPointerEvent<HTMLDivElement>,
  ) => {
    if (mobileLanguageCarouselPointerIdRef.current !== event.pointerId) {
      return;
    }
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    const currentIndex = activeMobileLanguageIndex >= 0 ? activeMobileLanguageIndex : 0;
    let nextIndex = currentIndex;
    if (mobileLanguageCarouselOffset <= -MOBILE_LANGUAGE_CAROUSEL_DRAG_THRESHOLD_PX) {
      nextIndex = (currentIndex + 1) % languageOptions.length;
    } else if (
      mobileLanguageCarouselOffset >= MOBILE_LANGUAGE_CAROUSEL_DRAG_THRESHOLD_PX
    ) {
      nextIndex =
        (currentIndex - 1 + languageOptions.length) % languageOptions.length;
    }

    const nextSpace = languageOptions[nextIndex];
    if (nextSpace && nextSpace.id !== languageSpace) {
      setLanguageSpace(nextSpace.id);
    }
    mobileLanguageCarouselPointerIdRef.current = null;
    setIsMobileLanguageCarouselDragging(false);
    setMobileLanguageCarouselOffset(0);
    window.setTimeout(() => {
      mobileLanguageCarouselMovedRef.current = false;
    }, 0);
  };
  const handleMobileLanguageCarouselItemClick = (spaceId: LanguageSpace) => {
    if (mobileLanguageCarouselMovedRef.current) {
      mobileLanguageCarouselMovedRef.current = false;
      return;
    }
    if (spaceId === languageSpace) {
      return;
    }
    setLanguageSpace(spaceId);
  };
  const mobileBottomNavItems = [
    {
      key: "texts",
      label: translation.translate("sidenav_texts"),
      icon: <BookOpenIcon className="h-5 w-5" />,
      active: pathname === "/app/reeprise" && !isOnMobileCreateView,
      onClick: () => {
        void navigate({
          to: "/app/reeprise",
          search: { mobileView: undefined },
        });
      },
      testId: "mobile-bottom-nav-texts",
    },
    {
      key: "progress",
      label: translation.translate("mobile_nav_word_archive"),
      icon: <FolderIcon className="h-5 w-5" />,
      active: pathname === "/app/vocabulary_progress",
      onClick: () => {
        void navigate({
          to: "/app/vocabulary_progress",
          search: { tab: undefined },
        });
      },
      testId: "mobile-bottom-nav-progress",
    },
    {
      key: "quick-translate",
      label: translation.translate("quick_translate_label"),
      icon: <PencilSquareIcon className="h-5 w-5" />,
      active: pathname === "/app/quick_translate",
      onClick: () => {
        void navigate({ to: "/app/quick_translate" });
      },
      testId: "mobile-bottom-nav-quick-translate",
    },
    {
      key: "profile",
      label: translation.translate("profile_title"),
      icon: <UserIcon className="h-5 w-5" />,
      active: pathname === "/app/profile",
      onClick: () => {
        void navigate({ to: "/app/profile" });
      },
      testId: "mobile-bottom-nav-profile",
    },
  ] as const;

  return (
    <div
      className="relative flex min-h-dvh w-full flex-col overflow-x-hidden bg-neutral-50 md:h-dvh md:overflow-hidden"
      style={
        {
          "--app-desktop-nav-width": navbarOpen ? "440px" : "72px",
          ...(showMobileLanguageCarousel
            ? { background: activeMobileLanguageTheme.pageWash }
            : {}),
        } as CSSProperties
      }
    >
      <div
        className={`relative z-40 items-center justify-between border border-slate-200 px-3 md:pr-4 md:pl-0 ${
          showMobileLanguageCarousel ? "hidden md:flex md:h-16" : "flex h-16"
        }`}
        style={
          showMobileLanguageCarousel
            ? { backgroundColor: "rgba(248,250,252,0.34)" }
            : undefined
        }
      >
        <div className="flex items-center">
          <div className="hidden h-16 items-center justify-center md:flex md:w-[72px] md:shrink-0 md:pl-0" />
          <div className="flex items-center gap-2 md:hidden">
            {shouldShowTopbarBackButton ? (
              <button
                type="button"
                aria-label="Go back"
                onClick={navigateBackFromTopbar}
                className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-300"
              >
                <ArrowLeftIcon className="h-5 w-5" />
              </button>
            ) : null}
          </div>
          <div
            className={`hidden md:flex md:items-center md:gap-2 md:pl-2 transition-opacity duration-75 ${topActionVisibilityClass}`}
          >
            {shouldShowTopbarBackButton ? (
              <button
                type="button"
                data-testid="desktop-topbar-leading-action"
                aria-label="Go back"
                onClick={navigateBackFromTopbar}
                className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-300"
              >
                <ArrowLeftIcon className="h-5 w-5" />
              </button>
            ) : (
              isOnMainAppPage ? (
                <Link
                  to="/app/profile"
                  data-testid="desktop-topbar-leading-action"
                  aria-label={translation.translate("profile_title")}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-300"
                >
                  <UserIcon className="w-5 h-5" />
                </Link>
              ) : null
            )}
          </div>
        </div>
        <div className="flex items-center gap-3 h-16 md:pr-4">
          {isOnTextDetailsPage ? (
            <button
              type="button"
              data-testid="topbar-text-details-clarify-trigger"
              aria-label={translation.translate("agent_helper_question_placeholder")}
              title={translation.translate("agent_helper_question_placeholder")}
              onClick={() => {
                window.dispatchEvent(
                  new CustomEvent("open-language-helper-overlay"),
                );
              }}
              className="inline-flex h-9 items-center gap-1 rounded-md border border-slate-200 bg-white px-2 text-slate-700 transition hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-300"
            >
              <QuestionMarkCircleIcon className="h-5 w-5" aria-hidden="true" />
              <span className="hidden text-xs font-semibold sm:inline">
                {translation.translate("agent_helper_question_placeholder")}
              </span>
            </button>
          ) : !isOnStartLearningPage ? (
            <Link
              to="/app/quick_translate"
              data-testid="quick-translate-topbar-link"
              aria-label={translation.translate("quick_translate_label")}
              title={translation.translate("quick_translate_label")}
              className="hidden h-9 w-9 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-300 md:inline-flex"
            >
              <PencilSquareIcon className="h-5 w-5" aria-hidden="true" />
            </Link>
          ) : null}
          <div
            className={`relative ${showLanguageSpaceSwitch ? "" : "hidden"} ${
              showMobileLanguageCarousel ? "hidden md:block" : ""
            }`}
            ref={languageMenuRef}
          >
            <button
              type="button"
              data-testid="topbar-language-space-switch"
              aria-label={translation.translate("language_space_switch")}
              onClick={() => setLanguageMenuOpen((prev) => !prev)}
              className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-300"
            >
              <span className="max-w-[8.5rem] truncate">{currentLanguageLabel}</span>
              <ChevronDownIcon
                className={`h-4 w-4 text-slate-500 transition ${
                  languageMenuOpen ? "rotate-180" : ""
                }`}
              />
            </button>
            {languageMenuOpen && (
              <div className="absolute right-0 z-50 mt-2 w-60 max-w-[calc(100vw-1.5rem)] rounded-2xl border border-slate-200 bg-white p-2 max-h-[70vh] overflow-y-auto">
                <div className="px-3 py-1 text-base uppercase tracking-wide text-slate-500">
                  {translation.translate("language_space_switch")}
                </div>
                <div className="mt-1 grid gap-1">
                  {languageOptions.map((opt) => {
                    const isActive = opt.id === languageSpace;
                    const isGreekTranscript =
                      opt.id === "GREEK_LATIN_TRANSCRIPTION";
                    const isRussianTranscript =
                      opt.id === "RUSSIAN_LATIN_TRANSCRIPTION";
                    const isPortugueseVariant =
                      opt.id === "PORTUGUESE_FROM_BRAZIL" ||
                      opt.id === "PORTUGUESE_FROM_PORTUGAL";
                    const optionLabel = isGreekTranscript
                      ? translation.translate("language_greek")
                      : isRussianTranscript
                        ? translation.translate("language_russian")
                        : isPortugueseVariant
                          ? translation.translate("language_portuguese")
                          : opt.label;
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => {
                          setLanguageSpace(opt.id);
                          setLanguageMenuOpen(false);
                        }}
                        className="flex items-center justify-start gap-3 rounded-xl px-3 py-2 text-base font-semibold text-left transition"
                        style={{
                          backgroundColor: isActive ? "#e2e8f0" : "#f8fafc",
                          color: "#0f172a",
                          border: "1px solid #e2e8f0",
                        }}
                      >
                        <span className="flex flex-col items-start leading-tight">
                          <span>{optionLabel}</span>
                          {(isGreekTranscript || isRussianTranscript) && (
                            <span className="text-base text-slate-500">
                              {translation.translate(
                                "language_variant_latin_transcription",
                              )}
                            </span>
                          )}
                          {isPortugueseVariant && (
                            <span className="text-base text-slate-500">
                              {translation.translate(
                                opt.id === "PORTUGUESE_FROM_BRAZIL"
                                  ? "language_variant_portuguese_brazil"
                                  : "language_variant_portuguese_portugal",
                              )}
                            </span>
                          )}
                        </span>
                      </button>
                    );
                  })}
                  {!hasAddedAllEnabledSpaces ? (
                    <button
                      type="button"
                      data-testid="add-language-space-option"
                      onClick={openAddLanguageSpaceFlow}
                      className="flex items-center justify-start gap-3 rounded-xl px-3 py-2 text-base font-semibold text-left text-slate-900 transition hover:bg-slate-50"
                      style={{
                        border: "1px dashed #cbd5e1",
                        backgroundColor: "#ffffff",
                      }}
                    >
                      <span>{translation.translate("space_settings_add_new_space")}</span>
                    </button>
                  ) : null}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {showMobileLanguageCarousel ? (
        <div className="relative overflow-hidden border-x border-b border-slate-200 px-3 py-2 md:hidden">
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0.05) 100%)",
            }}
          />
          <div
            className="relative h-[38px]"
            data-testid="mobile-language-space-carousel"
            onPointerDown={handleMobileLanguageCarouselPointerDown}
            onPointerMove={handleMobileLanguageCarouselPointerMove}
            onPointerUp={finishMobileLanguageCarouselGesture}
            onPointerCancel={finishMobileLanguageCarouselGesture}
            style={{ touchAction: "pan-y" }}
          >
            {languageOptions.map((opt, index) => {
              const carouselLabel = getMobileLanguageCarouselLabel(
                opt.id,
                opt.label,
                translation.translate,
              );
              const relativeDistance =
                getCircularStepDelta(
                  index,
                  activeMobileLanguageIndex >= 0 ? activeMobileLanguageIndex : 0,
                  languageOptions.length,
                ) +
                mobileLanguageCarouselOffset / MOBILE_LANGUAGE_CAROUSEL_STEP_PX;
              const clampedDistance = clampNumber(relativeDistance, -2, 2);
              const absoluteDistance = Math.abs(clampedDistance);
              const isActive = opt.id === languageSpace;
              const scale =
                absoluteDistance < 1
                  ? 1 - absoluteDistance * 0.12
                  : 0.88 - Math.min(absoluteDistance - 1, 1) * 0.08;
              const opacity =
                absoluteDistance < 1
                  ? 1 - absoluteDistance * 0.22
                  : 0.78 - Math.min(absoluteDistance - 1, 1) * 0.16;
              const translateX = clampedDistance * MOBILE_LANGUAGE_CAROUSEL_STEP_PX;
              const itemState = isActive
                ? "active"
                : absoluteDistance <= 1.15
                  ? "adjacent"
                  : "distant";
              return (
                <button
                  key={opt.id}
                  type="button"
                  data-testid={`mobile-language-space-carousel-space-${opt.id.toLowerCase()}`}
                  data-state={itemState}
                  aria-label={carouselLabel}
                  onClick={() => handleMobileLanguageCarouselItemClick(opt.id)}
                  className="absolute top-1/2 left-1/2 h-[30px] select-none px-2 font-TikTok text-[12px] tracking-[0.01em]"
                  style={{
                    width: `${MOBILE_LANGUAGE_CAROUSEL_CARD_WIDTH_PX}px`,
                    transform: `translate(-50%, -50%) translateX(${translateX}px) scale(${scale})`,
                    opacity,
                    zIndex: Math.max(1, 10 - Math.round(absoluteDistance * 4)),
                    backgroundColor: "transparent",
                    borderColor: "transparent",
                    color: isActive
                      ? activeMobileLanguageTheme.ink
                      : "rgba(51,65,85,0.78)",
                    transition: isMobileLanguageCarouselDragging
                      ? "none"
                      : "transform 260ms cubic-bezier(0.22, 1, 0.36, 1), opacity 200ms ease, color 200ms ease",
                  }}
                >
                  <span className="truncate">{carouselLabel}</span>
                </button>
              );
            })}
          </div>
          {languageOptions.length > 1 ? (
            <div
              className="relative flex items-center justify-center gap-1.5"
              data-testid="mobile-language-space-carousel-indicators"
            >
              {languageOptions.map((opt) => {
                const isActive = opt.id === languageSpace;
                return (
                  <span
                    key={opt.id}
                    data-testid={`mobile-language-space-carousel-indicator-${opt.id.toLowerCase()}`}
                    data-state={isActive ? "active" : "inactive"}
                    className={`block rounded-full transition-all duration-200 ${
                      isActive
                        ? "h-2 w-4"
                        : "h-1.5 w-1.5"
                    }`}
                    style={{
                      backgroundColor: isActive
                        ? activeMobileLanguageTheme.ink
                        : "rgba(100,116,139,0.3)",
                    }}
                  />
                );
              })}
            </div>
          ) : null}
        </div>
      ) : null}
      <div
        data-testid="topbar-sidenav-divider"
        className={`pointer-events-none fixed bottom-0 top-0 z-[70] hidden w-px bg-slate-200 transition-[left] duration-200 md:block ${
          navbarOpen ? "left-[440px]" : "left-[72px]"
        }`}
      />

      <div
        className="relative flex flex-1 min-h-0"
      >
        <div
          data-testid="side-navigation-shell"
          className={`hidden md:flex md:h-full md:min-h-0 md:shrink-0 md:overflow-hidden md:transition-[width] ${
            navbarOpen ? "md:w-[440px]" : "md:w-[72px]"
          }`}
        >
          <div
            data-testid="side-navigation-panel"
            className={`h-full min-h-0 w-full overflow-y-auto overscroll-contain bg-white md:flex md:min-h-0 md:flex-col md:border-b md:border-l md:border-slate-200 ${
              navbarOpen ? "pointer-events-auto md:w-[440px]" : "md:w-[72px]"
            }`}
          >
            <div className="px-2 py-3 space-y-4">
              <div className={`${navbarOpen ? "" : "flex justify-center"}`}>
                <button
                  type="button"
                  data-testid="navbar-toggle"
                  aria-label={translation.translate("website_nav_menu_toggle")}
                  onClick={handleNavbarToggle}
                  className={`group relative flex items-center rounded-xl text-slate-700 hover:bg-slate-50 transition ${
                    navbarOpen
                      ? "w-full gap-3 px-3 py-2 justify-start"
                      : "h-10 w-10 justify-center"
                  }`}
                >
                  <span
                    className={`inline-flex items-center gap-3 transition-opacity duration-150 group-hover:opacity-0 ${
                      navbarOpen ? "" : "absolute inset-0 justify-center"
                    }`}
                  >
                    <BrandLogo
                      className={`shrink-0 text-slate-700 ${
                        navbarOpen ? "h-6 w-6" : "h-7 w-7"
                      }`}
                    />
                    {navbarOpen ? (
                      <span className="text-base font-semibold">Uxhulu</span>
                    ) : null}
                  </span>
                  <span
                    className={`inline-flex transition-opacity duration-150 opacity-0 group-hover:opacity-100 ${
                      navbarOpen
                        ? "absolute left-3"
                        : "absolute inset-0 items-center justify-center"
                    }`}
                  >
                    {navbarOpen ? (
                      <ChevronLeftIcon className="h-5 w-5" />
                    ) : (
                      <ChevronRightIcon className="h-5 w-5" />
                    )}
                  </span>
                </button>
              </div>
              <div className="space-y-1">
                {(
                  [
                  {
                    link: "/",
                    text: "uxhulu.com",
                    pageTitle: translation.translate("sidenav_title_homepage"),
                    icon: <ArrowUpRightIcon className="w-5 h-5" />,
                  } as NavItem,
                  show_experimental
                    ? ({
                        link: "/app/account",
                        text: "Bark Account",
                        pageTitle: translation.translate("sidenav_title_account"),
                        icon: <BanknotesIcon className="w-5 h-5" />,
                      } as NavItem)
                    : undefined,
                  {
                    link: "/app/reeprise",
                    text: translation.translate("sidenav_texts"),
                    icon: <BookOpenIcon className="w-5 h-5" />,
                  } as NavItem,
                  show_experimental
                    ? ({
                        link: "/app/check_your_email",
                        text: "Email",
                        pageTitle: translation.translate("sidenav_title_check_email"),
                        icon: <EnvelopeIcon className="w-5 h-5" />,
                      } as NavItem)
                    : undefined,
                  {
                    link: "/app/vocabulary_progress",
                    text: translation.translate("link_vocabulary_progress"),
                    icon: <ChartBarIcon className="w-5 h-5" />,
                  } as NavItem,
                  {
                    link: "/app/product_logbook",
                    text: translation.translate("sidenav_product_updates"),
                    icon: <InformationCircleIcon className="w-5 h-5" />,
                  } as NavItem,
                  loginStatus.data.is_staff
                    ? ({
                        link: "/app/technical_change_log",
                        text: translation.translate(
                          "sidenav_technical_change_log",
                        ),
                        icon: <CommandLineIcon className="w-5 h-5" />,
                      } as NavItem)
                    : undefined,
                  show_experimental
                    ? ({
                        link: "/app/vocabulary_trainer_new",
                        text: "New Trainer",
                        pageTitle: "New Trainer",
                        icon: <AcademicCapIcon className="w-5 h-5" />,
                      } as NavItem)
                    : undefined,
                  {
                    link: "/app/settings",
                    text: translation.translate("sidenav_settings"),
                    icon: <Cog8ToothIcon className="w-5 h-5" />,
                  } as NavItem,
                ] as (NavItem | undefined)[]
              )
                .filter((obj): obj is NavItem => obj !== undefined)
                .map((obj) => (
                  <Link
                    key={obj.link}
                    to={obj.link}
                    aria-label={obj.text}
                    title={obj.text}
                    onClick={closeNavbarOnMobile}
                  >
                    <div
                      className={`flex items-center rounded-xl px-3 py-2 text-base font-semibold text-slate-700 hover:bg-slate-50 transition ${
                        navbarOpen
                          ? "gap-3 justify-start"
                          : "justify-center md:px-2"
                      }`}
                    >
                      {obj.icon}
                      <div className={navbarOpen ? "block" : "hidden md:hidden"}>
                        <p>{obj.text}</p>
                        {obj.pageTitle && obj.pageTitle !== obj.text ? (
                          <p className="text-xs font-medium text-slate-500">
                            {obj.pageTitle}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
              <div className="space-y-1 border-t border-slate-200 pt-3">
                {navbarOpen ? (
                  <div className="px-3 py-1">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      {translation.translate("sidenav_alpha_version")}
                    </p>
                    <p className="mt-1 text-xs leading-snug text-slate-500">
                      {translation.translate("sidenav_alpha_tools_hint")}
                    </p>
                  </div>
                ) : null}
                {navbarOpen ? (
                  <div className="px-3 py-2">
                    <ProgressBar />
                  </div>
                ) : null}
                <Link
                  to="/app/language_insights"
                  data-testid="language-insights-alpha-side-nav-link"
                  aria-label={translation.translate("sidenav_language_insights")}
                  title={translation.translate("sidenav_language_insights")}
                  onClick={closeNavbarOnMobile}
                >
                  <div
                    className={`flex items-center rounded-xl px-3 py-2 text-base font-semibold text-slate-700 hover:bg-slate-50 transition ${
                      navbarOpen ? "gap-3 justify-start" : "justify-center md:px-2"
                    }`}
                  >
                    <LanguageIcon className="w-5 h-5" />
                    <span className={navbarOpen ? "inline" : "hidden md:hidden"}>
                      {translation.translate("sidenav_language_insights")}
                    </span>
                  </div>
                </Link>
                <Link
                  to="/app/vocabulary_trainer"
                  data-testid="vocabulary-trainer-alpha-side-nav-link"
                  aria-label={translation.translate("link_vocabulary_list")}
                  title={translation.translate("link_vocabulary_list")}
                  onClick={closeNavbarOnMobile}
                >
                  <div
                    className={`flex items-center rounded-xl px-3 py-2 text-base font-semibold text-slate-700 hover:bg-slate-50 transition ${
                      navbarOpen ? "gap-3 justify-start" : "justify-center md:px-2"
                    }`}
                  >
                    <ClipboardDocumentListIcon className="w-5 h-5" />
                    <span className={navbarOpen ? "inline" : "hidden md:hidden"}>
                      {translation.translate("link_vocabulary_list")}
                    </span>
                  </div>
                </Link>
                <Link
                  to="/app/interest_map"
                  data-testid="interest-map-alpha-side-nav-link"
                  aria-label={translation.translate("sidenav_interest_map")}
                  title={translation.translate("sidenav_interest_map")}
                  onClick={closeNavbarOnMobile}
                >
                  <div
                    className={`flex items-center rounded-xl px-3 py-2 text-base font-semibold text-slate-700 hover:bg-slate-50 transition ${
                      navbarOpen ? "gap-3 justify-start" : "justify-center md:px-2"
                    }`}
                  >
                    <MapIcon className="w-5 h-5" />
                    <span className={navbarOpen ? "inline" : "hidden md:hidden"}>
                      {translation.translate("sidenav_interest_map")}
                    </span>
                  </div>
                </Link>
                <Link
                  to="/app/feature_mockups"
                  data-testid="feature-mockups-alpha-side-nav-link"
                  aria-label={translation.translate("sidenav_feature_mockups")}
                  title={translation.translate("sidenav_feature_mockups")}
                  onClick={closeNavbarOnMobile}
                >
                  <div
                    className={`flex items-center rounded-xl px-3 py-2 text-base font-semibold text-slate-700 hover:bg-slate-50 transition ${
                      navbarOpen ? "gap-3 justify-start" : "justify-center md:px-2"
                    }`}
                  >
                    <SparklesIcon className="w-5 h-5" />
                    <span className={navbarOpen ? "inline" : "hidden md:hidden"}>
                      {translation.translate("sidenav_feature_mockups")}
                    </span>
                  </div>
                </Link>
                <Link
                  to="/app/maison_memoire"
                  data-testid="maison-memoire-alpha-side-nav-link"
                  aria-label={translation.translate("sidenav_maison_memoire")}
                  title={translation.translate("sidenav_maison_memoire")}
                  onClick={closeNavbarOnMobile}
                >
                  <div
                    className={`flex items-center rounded-xl px-3 py-2 text-base font-semibold text-slate-700 hover:bg-slate-50 transition ${
                      navbarOpen ? "gap-3 justify-start" : "justify-center md:px-2"
                    }`}
                  >
                    <CameraIcon className="w-5 h-5" />
                    <span className={navbarOpen ? "inline" : "hidden md:hidden"}>
                      {translation.translate("sidenav_maison_memoire")}
                    </span>
                  </div>
                </Link>
                <button
                  type="button"
                  data-testid="speaking-practice-side-nav-link"
                  aria-label={translation.translate("sidenav_speaking_practice")}
                  title={
                    speakingTriggerCue.top
                      ? `${translation.translate("sidenav_speaking_practice")} · ${speakingTriggerCue.top.word}`
                      : translation.translate("sidenav_speaking_practice")
                  }
                  onClick={() => {
                    const trigger = maybeConsumeSpeakingTriggerCue();
                    setActiveSpeakingTrigger(trigger);
                    closeNavbarOnMobile();
                    void navigate({ to: "/app/dialogue" });
                  }}
                  className={`w-full flex items-center rounded-xl px-3 py-2 text-base font-semibold text-slate-700 hover:bg-slate-50 transition ${
                    navbarOpen ? "gap-3 justify-start" : "justify-center md:px-2"
                  }`}
                >
                  <span
                    className={`relative inline-flex h-5 w-5 shrink-0 items-center justify-center ${
                      showSpeakingCuePulse
                        ? speakingCueMode === "prominent"
                          ? "nav-speaking-entry-pulse nav-speaking-entry-pulse--prominent"
                          : "nav-speaking-entry-pulse"
                        : ""
                    }`}
                  >
                    <MicrophoneIcon className="h-5 w-5" />
                  </span>
                  <span className={navbarOpen ? "inline" : "hidden md:hidden"}>
                    {translation.translate("sidenav_speaking_practice")}
                  </span>
                </button>
              </div>
            {loginStatus.data.is_staff && (
              <a
                key="/admin"
                href="/admin"
                aria-label={translation.translate("sidenav_admin_area")}
                title={translation.translate("sidenav_admin_area")}
              >
                <div
                  className={`flex items-center rounded-xl px-3 py-2 text-base font-semibold text-slate-700 hover:bg-slate-50 transition ${
                    navbarOpen
                      ? "gap-3 justify-start"
                      : "justify-center md:px-2"
                  }`}
                >
                  <KeyIcon className="w-5 h-5" />
                  <span className={navbarOpen ? "inline" : "hidden md:hidden"}>
                    {translation.translate("sidenav_admin_area")}
                  </span>
                </div>
              </a>
            )}
            <div
              data-testid="logout-button"
              aria-label={translation.translate("logout_button")}
              title={translation.translate("logout_button")}
              className={`flex items-center rounded-xl px-3 py-2 text-base font-semibold text-slate-700 hover:bg-slate-50 transition cursor-pointer ${
                navbarOpen ? "gap-3 justify-start" : "justify-center md:px-2"
              }`}
              onClick={() => login.logoutMutation.mutate()}
            >
              <ArrowRightStartOnRectangleIcon className="w-5 h-5" />
              <span className={navbarOpen ? "inline" : "hidden md:hidden"}>
                {translation.translate("logout_button")}
              </span>
            </div>
            </div>
          </div>
        </div>
        <div
          data-app-content-scroll
          className={`flex-1 min-h-full overflow-y-visible md:min-h-0 md:overflow-y-auto md:overscroll-contain ${
            showMobileBottomNav
              ? "pb-[calc(6.5rem+env(safe-area-inset-bottom))] md:pb-0"
              : ""
          }`}
          style={
            showMobileLanguageCarousel
              ? { backgroundColor: "transparent" }
              : { backgroundColor: "#fafafa" }
          }
        >
          <Outlet />
        </div>
      </div>
      {showMobileBottomNav ? (
        <div
          className="fixed inset-x-0 bottom-0 z-40 flex items-end justify-center gap-3 px-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] md:hidden"
          data-testid="mobile-bottom-nav"
        >
          <div className="flex h-[58px] min-w-0 items-center gap-1 rounded-[28px] border border-slate-200 bg-white px-2 py-2">
            {mobileBottomNavItems.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={item.onClick}
                aria-label={item.label}
                data-testid={item.testId}
                className={`inline-flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-full p-0 transition-colors ${
                  item.active
                    ? "bg-slate-100 text-slate-950"
                    : "text-slate-500"
                }`}
              >
                {item.icon}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => {
              void navigate({
                to: "/app/reeprise",
                search: { mobileView: "create" },
              });
            }}
            aria-label={translation.translate("mobile_nav_create")}
            data-testid="mobile-bottom-nav-create"
            className={`inline-flex h-[58px] w-[58px] min-h-[58px] min-w-[58px] shrink-0 aspect-square items-center justify-center rounded-full border p-0 leading-none transition-colors ${
              isOnMobileCreateView
                ? "border-slate-900 bg-slate-900 text-white"
                : "border-slate-200 bg-white text-slate-950"
            }`}
          >
            <PlusIcon className="h-5 w-5" />
          </button>
        </div>
      ) : null}
      <SnackBar />
    </div>
  );
}
